import type { PlacementComponent } from '@post-office/placement-contexts/create-placement-component/types';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { createPlacementComponent } from '@post-office/placement-common/utils/create-placement-component';

import { useInitialData } from '@post-office/placement-contexts/hooks';

import React, { useEffect } from 'react';

import { MessageRenderer } from '../__generated__/client/render';

import { PLACEMENT_ID } from '../__generated__/common';

import { withJiraIssueviewCommentIntlProvider } from '../__generated__/intl-provider';

import {
	type JiraIssueviewCommentInitialData,
	type JiraIssueviewCommentMessage,
} from '../api/types';

import { type Placement as JiraIssueviewCommentPlacement } from '../contracts/Placement';

export const JiraIssueviewCommentPlacementComponent: PlacementComponent<Record<string, unknown>> =
	withJiraIssueviewCommentIntlProvider(
		createPlacementComponent<JiraIssueviewCommentPlacement>(
			PLACEMENT_ID,
			({ initialData, onClick, onDismissed, onAdsShown }) => {
				const { isLoading, data } = useInitialData<JiraIssueviewCommentInitialData>(initialData);
				const [messages, setMessages] = React.useState<JiraIssueviewCommentMessage[]>([]);
				const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

				const handleAdDismissed = React.useCallback(
					(messageTemplateId: string) => {
						setMessages((prevMessages) =>
							prevMessages.filter((message) => message.messageTemplateId !== messageTemplateId),
						);
						onDismissed(messageTemplateId);
						fireAnalyticsEvent({
							eventType: 'ui',
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'comment-xflow-dismiss',
							attributes: {
								messageTemplateId,
							},
						});
					},
					[onDismissed, fireAnalyticsEvent],
				);

				const handleAdClick = React.useCallback(
					(messageTemplateId: string) => {
						onClick(messageTemplateId);
						fireAnalyticsEvent({
							eventType: 'ui',
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'comment-xflow-click',
							attributes: {
								messageTemplateId,
							},
						});
					},
					[onClick, fireAnalyticsEvent],
				);

				useEffect(() => {
					if (data?.messages.length) {
						const visibleAds = data.messages.map((message) => message.messageTemplateId);
						onAdsShown(visibleAds);
						setMessages(data.messages);
						const attributes = data.messages.reduce<Record<string, boolean>>((acc, val) => {
							acc[val.messageTemplateId] = true;
							return acc;
						}, {});
						fireAnalyticsEvent({
							eventType: 'screen',
							name: 'comment-xflow',
							attributes,
						});
					} else {
						setMessages([]);
					}
				}, [data?.messages, onAdsShown]);

				if (isLoading || !data?.messages.length) {
					return null;
				}

				return (
					<div data-testid="jira-issueview-comment-placement">
						{messages.map((message) => (
							<MessageRenderer
								{...message}
								id={message.messageInstanceId}
								onDismissed={handleAdDismissed}
								onClick={handleAdClick}
							/>
						))}
					</div>
				);
			},
		),
	);

export default JiraIssueviewCommentPlacementComponent;
